import { useState } from "react";
import { ImagePlus, Loader2, Download, X } from "lucide-react";
import { toast } from "sonner";
import { generateAiImage } from "@/lib/api";

const STYLES = [
  { key: "product", label: "Studio" },
  { key: "thumbnail", label: "Thumbnail" },
  { key: "lifestyle", label: "Lifestyle" },
  { key: "logo", label: "Logo" },
];

/**
 * Generates a listing-ready mockup for an idea/product title using gpt-image-1.
 * Shows the result inline with a download link; the file is also archived to
 * object storage so it appears in the user's AI gallery.
 */
export const MockupButton = ({ prompt, defaultStyle = "product", label = "Mockup", testId = "mockup-button" }) => {
  const [loading, setLoading] = useState(false);
  const [img, setImg] = useState(null);
  const [style, setStyle] = useState(defaultStyle);
  const [open, setOpen] = useState(false);

  const run = async (s) => {
    setLoading(true);
    setOpen(true);
    try {
      const r = await generateAiImage(prompt, s);
      setImg(`data:${r.mime_type};base64,${r.image_base64}`);
      if (!r.archived) toast.message("Generated — but not saved to your gallery");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Image generation failed");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid={testId}
        onClick={() => run(style)}
        disabled={loading || !prompt}
        className="flex items-center gap-1.5 text-[10px] tracking-[0.3em] uppercase text-[#34d399] hover:text-white border border-[#34d399]/40 hover:border-[#34d399] px-3 py-1.5 transition-colors disabled:opacity-50"
      >
        {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImagePlus className="h-3 w-3" />}
        {loading ? "Rendering…" : label}
      </button>

      {open && (
        <div className="mt-3 border border-zinc-800 bg-black/60 p-3" data-testid={`${testId}-panel`}>
          <div className="flex items-center justify-between gap-2">
            <div className="flex flex-wrap gap-1.5">
              {STYLES.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  data-testid={`${testId}-style-${s.key}`}
                  onClick={() => { setStyle(s.key); setImg(null); run(s.key); }}
                  disabled={loading}
                  className={`text-[9px] tracking-[0.25em] uppercase border px-2 py-1 font-mono transition-colors disabled:opacity-50 ${
                    style === s.key ? "border-[#34d399] text-[#34d399]" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              data-testid={`${testId}-close`}
              onClick={() => setOpen(false)}
              className="text-zinc-500 hover:text-white"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="mt-3 aspect-square w-full max-w-[280px] border border-zinc-800 bg-zinc-950 flex items-center justify-center overflow-hidden">
            {loading && <Loader2 className="h-6 w-6 animate-spin text-[#34d399]" />}
            {!loading && img && (
              <img src={img} alt={prompt} className="h-full w-full object-cover" data-testid={`${testId}-image`} />
            )}
            {!loading && !img && <span className="font-mono text-[10px] text-zinc-500">// no render yet</span>}
          </div>

          {img && (
            <a
              href={img}
              download="moneyhub-mockup.png"
              data-testid={`${testId}-download`}
              className="mt-3 inline-flex items-center gap-1.5 text-[10px] tracking-[0.3em] uppercase text-[#60a5fa] hover:text-white border border-[#60a5fa]/40 hover:border-[#60a5fa] px-3 py-1.5 transition-colors"
            >
              <Download className="h-3 w-3" /> Download
            </a>
          )}
        </div>
      )}
    </>
  );
};

export default MockupButton;
